import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const PIPELINE_STEPS = [
  { icon: '📝', title: 'Paste', desc: 'Drop any vague requirement, policy, or spec' },
  { icon: '🔍', title: 'Agent 1: Detect', desc: 'AI scans for ambiguous phrases and vague language' },
  { icon: '📊', title: 'Agent 2: Assess', desc: 'Scores risk, explains the score, flags consequences' },
  { icon: '✍️', title: 'Agent 3: Rewrite', desc: 'Produces a precision spec with testable criteria' },
  { icon: '✅', title: 'Ship', desc: 'Clear, measurable, ready for development' },
];

const FEATURES = [
  { icon: '🎯', title: 'Ambiguity Scoring', desc: '0-100 score with explainable breakdown' },
  { icon: '🚩', title: 'Risk Flag Detection', desc: 'Identifies concrete operational consequences' },
  { icon: '🧠', title: 'Multi-Agent Pipeline', desc: '3 specialized AI agents, not a single prompt' },
  { icon: '✅', title: 'Acceptance Criteria', desc: 'Auto-generated testable pass/fail conditions' },
  { icon: '✏️', title: 'Precision Rewriting', desc: 'Every vague word replaced with measurable language' },
  { icon: '🔌', title: 'API-First Architecture', desc: 'Any tool can POST text and receive structured risk analysis as JSON' },
];

const PERSONAS = [
  { icon: '🧑‍💼', title: 'Engineering Managers', desc: 'Catch vague tickets before sprint planning — saves 40+ hours per cycle' },
  { icon: '🏛️', title: 'Government Program Officers', desc: 'Analyze circulars and policy directives for implementation gaps' },
  { icon: '⚖️', title: 'Compliance & Legal Teams', desc: 'Flag ambiguous contract clauses and SLA language before signing' },
];

const INTEGRATION_PATHS = [
  { icon: '📡', title: 'Direct API', desc: 'POST any text to our endpoint, get structured risk JSON back. Works today. Zero setup.', badge: '✅ Live', badgeColor: '#10b981' },
  { icon: '⚡', title: 'Automation Layer', desc: 'Connect via Zapier, Make, or n8n. Jira creates a ticket → webhook triggers analysis → results posted back as a comment.', badge: '🔜 Next', badgeColor: '#f59e0b' },
  { icon: '🌐', title: 'Browser Extension', desc: 'Select text on any page — Jira, Confluence, Google Docs — right-click "Analyze with UnVague". No admin consent required.', badge: '🔜 Planned', badgeColor: '#f59e0b' },
  { icon: '🔗', title: 'Native Plugin', desc: 'Marketplace apps for Jira, Azure DevOps, Linear. An "Analyze" button inside your existing workflow.', badge: '🗺️ Roadmap', badgeColor: '#71717a' },
];

const DATA_TIERS = [
  { label: 'Current — Free Tier', title: 'Stateless Processing', desc: 'Text in → analysis out. Nothing stored on our side. Your data never leaves your systems. Zero compliance burden.', active: true },
  { label: 'Future — Team Plan', title: 'Analysis Storage Only', desc: 'Original text stays in your systems. We store scores, flags, and trends for team dashboards. Minimal data footprint.', active: false },
  { label: 'Future — Enterprise', title: 'Full Persistence', desc: 'Full history for cross-ticket conflict detection, pattern analysis, and organizational risk trends. SOC 2 compliant.', active: false },
];

function Collapsible({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid #1f1f2e', borderRadius: 10, marginBottom: 16 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 18px', color: '#a1a1aa', fontSize: 13, fontWeight: 600,
          fontFamily: 'inherit',
        }}
      >
        {title}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div style={{ padding: '0 18px 18px' }}>{children}</div>}
    </div>
  );
}

export default function HowItWorksTab() {
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '32px 24px' }} className="scrollbar-hide">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        <div style={{ marginBottom: 40 }}>
          <h2 style={{ color: '#f4f4f5', fontSize: 22, fontWeight: 700, marginBottom: 24, letterSpacing: '-0.02em' }}>
            The 3-Agent Pipeline
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto' }} className="scrollbar-hide">
            {PIPELINE_STEPS.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <div style={{
                  background: '#141419', border: '1px solid #1f1f2e',
                  borderRadius: 12, padding: '16px 18px', textAlign: 'center',
                  minWidth: 140, transition: 'border-color 0.2s',
                }}
                  className="feature-card"
                >
                  <div style={{ fontSize: 26, marginBottom: 8 }}>{step.icon}</div>
                  <div style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{step.title}</div>
                  <div style={{ color: '#52525b', fontSize: 11, lineHeight: 1.4 }}>{step.desc}</div>
                </div>
                {i < PIPELINE_STEPS.length - 1 && (
                  <div style={{ padding: '0 6px', color: '#3f3f46', fontSize: 18, flexShrink: 0 }} className="pipeline-arrow">
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 40 }}>
          <h2 style={{ color: '#f4f4f5', fontSize: 22, fontWeight: 700, marginBottom: 20, letterSpacing: '-0.02em' }}>
            What UnVague Does
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {FEATURES.map((f, i) => (
              <div key={i} className="feature-card" style={{
                background: '#141419', border: '1px solid #1f1f2e',
                borderRadius: 10, padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>{f.icon}</span>
                <div>
                  <div style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{f.title}</div>
                  <div style={{ color: '#52525b', fontSize: 12, lineHeight: 1.5 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 40, background: '#141419', border: '1px solid #1f1f2e', borderRadius: 12, padding: '24px' }}>
          <h3 style={{ color: '#f4f4f5', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            Integration Ready — API-First by Design
          </h3>
          <div className="code-block">{`POST /api/analyze
Content-Type: application/json

{
  "text": "Your vague requirement here..."
}

→ Returns structured JSON:
{
  "ambiguityScore": 78,
  "riskLevel": "high",
  "riskFlags": [...],
  "scoreBreakdown": [...],
  "clarifyingQuestions": [...],
  "rewrite": "...",
  "acceptanceCriteria": [...]
}`}</div>
          <div style={{ color: '#52525b', fontSize: 12, marginTop: 14, lineHeight: 1.6 }}>
            Any system that makes HTTP calls — Jira, Azure DevOps, CI/CD pipelines, Slack bots, Zapier — can use this endpoint today.
          </div>
        </div>

        <div style={{ marginBottom: 32, background: '#141419', border: '1px solid #1f1f2e', borderRadius: 10, padding: '16px 20px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 18 }}>🔒</span>
          <div style={{ color: '#71717a', fontSize: 13, lineHeight: 1.6 }}>
            <strong style={{ color: '#a1a1aa' }}>Stateless by design.</strong> UnVague processes and returns — your data stays in your systems. No PII stored. No data residency concerns. Enterprise-grade privacy from day one.
          </div>
        </div>

        <div style={{ marginBottom: 36 }}>
          <h2 style={{ color: '#f4f4f5', fontSize: 22, fontWeight: 700, marginBottom: 20, letterSpacing: '-0.02em' }}>
            Who Is This For?
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {PERSONAS.map((p, i) => (
              <div key={i} className="feature-card" style={{
                background: '#141419', border: '1px solid #1f1f2e',
                borderRadius: 10, padding: '18px', display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <span style={{ fontSize: 28 }}>{p.icon}</span>
                <div style={{ color: '#f4f4f5', fontSize: 14, fontWeight: 700 }}>{p.title}</div>
                <div style={{ color: '#52525b', fontSize: 12, lineHeight: 1.5 }}>{p.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{
          marginBottom: 32, padding: '28px 32px', textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.05) 100%)',
          border: '1px solid rgba(99,102,241,0.2)', borderRadius: 14,
        }}>
          <p style={{ color: '#a1a1aa', fontSize: 15, lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>
            "Ambiguity is the #1 cause of engineering rework, compliance failures, and operational risk. UnVague is the first multi-agent system that detects it, scores it, and fixes it — before code is written."
          </p>
        </div>

        <Collapsible title="🔌 How It Plugs Into Your Workflow">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {INTEGRATION_PATHS.map((ip, i) => (
              <div key={i} style={{ background: '#141419', border: '1px solid #1f1f2e', borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>{ip.icon}</span>
                  <span style={{ color: ip.badgeColor, fontSize: 10, fontWeight: 600 }}>{ip.badge}</span>
                </div>
                <div style={{ color: '#f4f4f5', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{ip.title}</div>
                <div style={{ color: '#52525b', fontSize: 11, lineHeight: 1.5 }}>{ip.desc}</div>
              </div>
            ))}
          </div>
        </Collapsible>

        <Collapsible title="🔒 Where Does Your Data Live?">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {DATA_TIERS.map((tier, i) => (
              <div key={i} style={{
                flex: '1 1 200px',
                background: tier.active ? 'rgba(99,102,241,0.08)' : '#141419',
                border: `1px solid ${tier.active ? 'rgba(99,102,241,0.3)' : '#1f1f2e'}`,
                borderRadius: 8, padding: '14px 16px',
              }}>
                <div style={{ color: tier.active ? '#818cf8' : '#52525b', fontSize: 10, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {tier.label}
                </div>
                <div style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{tier.title}</div>
                <div style={{ color: '#52525b', fontSize: 11, lineHeight: 1.5 }}>{tier.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ color: '#3f3f46', fontSize: 11, marginTop: 12, textAlign: 'center' }}>
            You choose the level. We believe in privacy by default — we only store what you explicitly opt into.
          </div>
        </Collapsible>

      </div>
    </div>
  );
}
